// @flow
import {
  type ParsingOptions,
  type CellObject as Cell,
  type WorkSheet,
  type CellAddress,
  type WorkBook,
  read,
  utils,
} from 'xlsx';

import { withAdjustment } from 'modules/date';

export {
  ParsingOptions,
  WorkBook,
  WorkSheet,
  CellObject as Cell,
  CellAddress,
  Range,
} from 'xlsx';

export const parsed = (
  file: Blob,
  opts?: ParsingOptions = { ...null },
): Promise<WorkBook> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', (e: ProgressEvent) => {
      const data = new Uint8Array((e.target: any).result);

      try {
        const wb = read(data, {
          cellDates: true,
          ...opts,
          type: 'array',
        });
        resolve(wb);
      } catch (error) {
        reject(error);
      }
    });

    reader.readAsArrayBuffer(file);
  });

export const parsedSynchronously = (
  data: mixed,
  opts?: ParsingOptions = { ...null },
): WorkBook =>
  read(data, {
    ...opts,
  });

export const cell =
  (sheet: WorkSheet): ((address: CellAddress) => Cell | null) =>
  (address: CellAddress): Cell | null => {
    const ref = utils.encode_cell(address);
    return sheet[ref] || null;
  };

export const worksheetRowCount = (sheet: WorkSheet): number =>
  sheet['!ref'] ? utils.decode_range(sheet['!ref']).e.r + 1 : 0;

export const worksheetColumnCount = (sheet: WorkSheet): number =>
  sheet['!ref'] ? utils.decode_range(sheet['!ref']).e.c + 1 : 0;

export const simpleCellValue = (c: Cell): string | number | boolean | Date => {
  switch (c.t) {
    case 's':
      return c.v;
    case 'n':
      return c.v;
    case 'e':
      return '';
    case 'd': {
      const value = c.v;
      // in some timezones the date is parsed by SheetJS one day earlier - by a few seconds
      // this is a known issue with SheetJS https://github.com/SheetJS/sheetjs/issues/1470
      // because we're parsing shipment dates out of OLOF files,
      // the year, month and day are all that matters
      // https://github.com/ElasticSuite/scramble4/issues/10900
      return value instanceof Date
        ? withAdjustment({ hours: value.getHours() + 1 }, value)
        : value;
    }
    case 'b':
      return c.v;
    case 'z':
      return '';
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (c.t: empty);
      throw new Error(`Unhandled CSFCell type: ${c.t}`);
  }
};

export const cellIsNumber = (c: Cell): boolean => c.t === 'n';

export const cellHtmlRendering = (c: Cell): string | null => c.h || null;

export const cellFormattedText = (c: Cell): string | null => c.w || null;

export const headers = (workbook: WorkSheet): $ReadOnlyArray<Cell> =>
  Array(worksheetColumnCount(workbook))
    .fill(null)
    .map((_, idx) => cell(workbook)({ c: idx, r: 0 }))
    .filter(Boolean);

export const columnByHeaderGuesses =
  (
    workbook: WorkSheet,
  ): ((guesses: $ReadOnlyArray<string | RegExp>) => number | null) =>
  (guesses: $ReadOnlyArray<string | RegExp>): number | null =>
    guesses.reduce((acc, guess) => {
      if (typeof acc === 'number') return acc;

      const found = headers(workbook).findIndex((headerCell) =>
        Boolean(String(simpleCellValue(headerCell)).trim().match(guess)),
      );

      return found === -1 ? null : found;
    }, null);

export const workbookGivenHeaderAndDetails = (
  headerAndDetails: $ReadOnlyArray<$ReadOnlyArray<string>>,
  sheetName: string,
): WorkBook => {
  // Sheet.js enforces a Microsoft-based "Sheet names cannot exceed 31 chars" rule.
  const maxLength = 31;
  const worksheet = utils.aoa_to_sheet(headerAndDetails);
  const workbook = utils.book_new();
  utils.book_append_sheet(
    workbook,
    worksheet,
    sheetName.substring(0, maxLength),
  );
  return workbook;
};
